import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { type RouteObject } from 'react-router-dom';

import { AppLayout } from './(app)/_layout';
import { ModalRouteWrapper } from './(app)/_modal-routes';
import { AuthLayout } from './(auth)/_layout';
import { LegalLayout } from './(legal)/_layout';
import { GallerySuspense } from './_dev-gallery';
import { RootLayout } from './_layout';
import { RootRedirect } from './_root-redirect';
import { NotFound } from './not-found';

import { Skeleton } from '@/shared/components/ui/skeleton';

// Lazy code-split das telas de auth — diminui o bundle do shell autenticado
// (que carrega imediato no boot). Telas auth so aparecem no fluxo de login.
const WelcomeScreen = lazy(() => import('./(auth)/welcome'));
const AppLinksScreen = lazy(() => import('./app-links'));
const LoginScreen = lazy(() => import('./(auth)/login'));
const RegisterScreen = lazy(() => import('./(auth)/register'));
const ForgotPasswordScreen = lazy(() => import('./(auth)/forgot-password'));
const ResetPasswordScreen = lazy(() => import('./(auth)/reset-password'));
// Phase 19 — landing publica de Universal Links / App Links de convite.
const InviteLandingPage = lazy(() => import('./(auth)/invite'));

// Paginas legais publicas (/legal/*). Uma tela serve os 3 documentos via prop
// `slug`. URL publica exigida pelas lojas (substitui o HTML estatico).
const LegalDocScreenPage = lazy(() => import('./(legal)/legal-doc-screen'));

// Lazy das telas autenticadas pesadas — Phase 06 introduz home/transactions/expenses.
// Modais sao carregados via lazy tambem; quando abertos como Sheet ainda
// passam pelo Suspense, mas o conteudo e enxuto (form simples).
const HomeScreen = lazy(() => import('./(app)/home'));
const CreateTransactionScreen = lazy(() => import('./(app)/transactions/create-transaction'));
const AddExpenseScreen = lazy(() => import('./(app)/transactions/expenses/new'));
const AddIncomeScreen = lazy(() => import('./(app)/transactions/incomes/new'));
const ExpenseDetailScreen = lazy(() => import('./(app)/expenses/detail'));
const IncomeDetailScreen = lazy(() => import('./(app)/incomes/detail'));
// Phase 07 — categorias + income-sources (CRUD).
const ManageCategoriesScreen = lazy(() => import('./(app)/transactions/manage-categories'));
const ManageIncomeSourcesScreen = lazy(() => import('./(app)/transactions/manage-income-sources'));

// Phase 14 — wizard de imports (CSV de fatura/extrato).
const ImportsScreen = lazy(() => import('./(app)/transactions/imports'));

// Phase 08 — family-groups (lista, create, join, detalhe).
const GroupsListPage = lazy(() => import('./(app)/groups/index'));
const CreateGroupPage = lazy(() => import('./(app)/groups/create'));
const JoinGroupPage = lazy(() => import('./(app)/groups/join'));
const GroupDetailPage = lazy(() => import('./(app)/groups/detail'));

// Phase 09 — family-groups financial (saldos, contribuicao, settlements).
const BalancesPage = lazy(() => import('./(app)/groups/balances'));
const ContributionHistoryPage = lazy(() => import('./(app)/groups/contribution-history'));
const SettlementsListPage = lazy(() => import('./(app)/groups/settlements-list'));
const NewSettlementPage = lazy(() => import('./(app)/groups/new-settlement'));
const SettlementDetailPage = lazy(() => import('./(app)/groups/settlement-detail'));

// Phase 10 — reports (dashboard + history). Charts pesados (recharts) sao
// code-splitted dentro de DashboardScreen; o lazy aqui garante tambem que a
// tela so chega ao bundle do shell quando o usuario navega para /reports.
const ReportsDashboardPage = lazy(() => import('./(app)/reports/dashboard'));
const ReportsHistoryPage = lazy(() => import('./(app)/reports/history'));

// Phase 11 — budgets + forecasts. Read-write em budgets, read-only em forecasts.
const ReportsBudgetsPage = lazy(() => import('./(app)/reports/budgets'));
const ReportsForecastsPage = lazy(() => import('./(app)/reports/forecasts'));

// Phase 12 — notifications (list + preferences).
const NotificationsListPage = lazy(() => import('./(app)/notifications/list'));
const NotificationsPreferencesPage = lazy(() => import('./(app)/notifications/preferences'));

// Phase 13 — profile (hub, edit, settings, lgpd).
const ProfileScreenPage = lazy(() => import('./(app)/profile/index'));
const EditProfilePage = lazy(() => import('./(app)/profile/edit'));
const SettingsPage = lazy(() => import('./(app)/profile/settings'));
const LgpdPage = lazy(() => import('./(app)/profile/lgpd'));

function withSuspense<P extends object>(
  Component: ComponentType<P>,
  variant: 'auth' | 'app' = 'app',
): (props: P) => ReactNode {
  return function LazySuspense(props: P) {
    return (
      <Suspense
        fallback={
          variant === 'auth' ? (
            <div
              className="flex min-h-screen items-center justify-center bg-canvas px-6"
              data-testid="auth-screen-loading"
              role="status"
              aria-label="Carregando tela"
            >
              <Skeleton height={48} className="w-56" />
            </div>
          ) : (
            <div
              className="space-y-3"
              data-testid="app-screen-loading"
              role="status"
              aria-label="Carregando tela"
            >
              <Skeleton height={32} className="w-1/2" />
              <Skeleton height={120} className="w-full" />
              <Skeleton height={64} className="w-full" />
            </div>
          )
        }
      >
        <Component {...props} />
      </Suspense>
    );
  };
}

const Welcome = withSuspense(WelcomeScreen, 'auth');
const AppLinks = withSuspense(AppLinksScreen, 'auth');
const Login = withSuspense(LoginScreen, 'auth');
const Register = withSuspense(RegisterScreen, 'auth');
const ForgotPassword = withSuspense(ForgotPasswordScreen, 'auth');
const ResetPassword = withSuspense(ResetPasswordScreen, 'auth');
const InviteLanding = withSuspense(InviteLandingPage, 'auth');
const LegalDoc = withSuspense(LegalDocScreenPage, 'auth');

const Home = withSuspense(HomeScreen, 'app');
const CreateTransaction = withSuspense(CreateTransactionScreen, 'app');
const AddExpense = withSuspense(AddExpenseScreen, 'app');
const AddIncome = withSuspense(AddIncomeScreen, 'app');
const ExpenseDetail = withSuspense(ExpenseDetailScreen, 'app');
const IncomeDetail = withSuspense(IncomeDetailScreen, 'app');
const ManageCategories = withSuspense(ManageCategoriesScreen, 'app');
const ManageIncomeSources = withSuspense(ManageIncomeSourcesScreen, 'app');
const Imports = withSuspense(ImportsScreen, 'app');
const GroupsList = withSuspense(GroupsListPage, 'app');
const CreateGroup = withSuspense(CreateGroupPage, 'app');
const JoinGroup = withSuspense(JoinGroupPage, 'app');
const GroupDetail = withSuspense(GroupDetailPage, 'app');
const Balances = withSuspense(BalancesPage, 'app');
const ContributionHistory = withSuspense(ContributionHistoryPage, 'app');
const SettlementsList = withSuspense(SettlementsListPage, 'app');
const NewSettlement = withSuspense(NewSettlementPage, 'app');
const SettlementDetail = withSuspense(SettlementDetailPage, 'app');
const ReportsDashboard = withSuspense(ReportsDashboardPage, 'app');
const ReportsHistory = withSuspense(ReportsHistoryPage, 'app');
const ReportsBudgets = withSuspense(ReportsBudgetsPage, 'app');
const ReportsForecasts = withSuspense(ReportsForecastsPage, 'app');
const NotificationsList = withSuspense(NotificationsListPage, 'app');
const NotificationsPreferences = withSuspense(NotificationsPreferencesPage, 'app');
const Profile = withSuspense(ProfileScreenPage, 'app');
const EditProfile = withSuspense(EditProfilePage, 'app');
const Settings = withSuspense(SettingsPage, 'app');
const Lgpd = withSuspense(LgpdPage, 'app');

const authRoutes: RouteObject[] = [
  { path: 'welcome', element: <Welcome /> },
  { path: 'login', element: <Login /> },
  { path: 'register', element: <Register /> },
  { path: 'forgot-password', element: <ForgotPassword /> },
  { path: 'reset-password', element: <ResetPassword /> },
  // Phase 19 — landing publica de convite (path /invite/:code). Vive no
  // grupo (auth) por usar o mesmo `AuthLayout` minimal e a estilizacao
  // base (`AuthPageShell`). NAO requer auth — o componente lida com os
  // dois caminhos (logado / nao logado).
  { path: 'invite/:code', element: <InviteLanding /> },
];

const appRoutes: RouteObject[] = [
  { path: 'home', element: <Home /> },
  { path: 'expenses/:id', element: <ExpenseDetail /> },
  { path: 'incomes/:id', element: <IncomeDetail /> },

  // Modais — wrapping em ModalRouteWrapper escolhe entre Sheet (quando
  // abertas via state.backgroundLocation) e pagina full (deep-link direto).
  {
    path: 'transactions/new',
    element: (
      <ModalRouteWrapper title="Nova transação" testID="modal-create-transaction">
        <CreateTransaction />
      </ModalRouteWrapper>
    ),
  },
  {
    path: 'transactions/expenses/new',
    element: (
      <ModalRouteWrapper title="Nova despesa" testID="modal-add-expense">
        <AddExpense />
      </ModalRouteWrapper>
    ),
  },
  {
    path: 'transactions/incomes/new',
    element: (
      <ModalRouteWrapper title="Nova receita" testID="modal-add-income">
        <AddIncome />
      </ModalRouteWrapper>
    ),
  },
  {
    path: 'transactions/categories',
    element: (
      <ModalRouteWrapper title="Categorias" testID="modal-manage-categories" mobileFullHeight>
        <ManageCategories />
      </ModalRouteWrapper>
    ),
  },
  {
    path: 'transactions/income-sources',
    element: (
      <ModalRouteWrapper title="Fontes de receita" testID="modal-manage-income-sources">
        <ManageIncomeSources />
      </ModalRouteWrapper>
    ),
  },
  {
    path: 'transactions/imports',
    element: (
      <ModalRouteWrapper title="Importar despesas" testID="modal-imports">
        <Imports />
      </ModalRouteWrapper>
    ),
  },

  { path: 'groups', element: <GroupsList /> },
  { path: 'groups/create', element: <CreateGroup /> },
  { path: 'groups/join', element: <JoinGroup /> },
  { path: 'groups/:id', element: <GroupDetail /> },
  { path: 'groups/:id/balances', element: <Balances /> },
  { path: 'groups/:id/contribution-history', element: <ContributionHistory /> },
  { path: 'groups/:id/settlements', element: <SettlementsList /> },
  { path: 'groups/:id/settlements/new', element: <NewSettlement /> },
  { path: 'groups/:id/settlements/:settlementId', element: <SettlementDetail /> },

  { path: 'reports', element: <ReportsDashboard /> },
  { path: 'reports/history', element: <ReportsHistory /> },
  { path: 'reports/budgets', element: <ReportsBudgets /> },
  { path: 'reports/forecasts', element: <ReportsForecasts /> },

  { path: 'profile', element: <Profile /> },
  { path: 'profile/edit', element: <EditProfile /> },
  { path: 'profile/settings', element: <Settings /> },
  { path: 'profile/lgpd', element: <Lgpd /> },

  { path: 'notifications', element: <NotificationsList /> },
  { path: 'notifications/preferences', element: <NotificationsPreferences /> },
];

// Paginas legais publicas (sem auth). URLs limpas: /legal/privacy, /legal/terms,
// /legal/account-deletion. Uma unica tela renderiza cada documento via `slug`.
const legalRoutes: RouteObject[] = [
  { path: 'legal/privacy', element: <LegalDoc slug="privacy" /> },
  { path: 'legal/terms', element: <LegalDoc slug="terms" /> },
  { path: 'legal/account-deletion', element: <LegalDoc slug="account-deletion" /> },
];

// =============================================================================
// DEV-ONLY: rotas dev (gallery + mocks da Regra de 3 Mocks).
//
// Toda a inicializacao das rotas dev (incluindo os ~80 `lazy(() => import(...))`
// das telas de mock) vive DENTRO do `if (import.meta.env.DEV)`. No build de
// producao, `import.meta.env.DEV` e substituido por `false` pelo Vite via
// `define`, e o Rollup elimina o bloco inteiro (dead code elimination). Isso
// garante que MSW handlers, factories de fixtures e os ~97 arquivos
// `__mocks__/**/*.tsx` NAO entrem no bundle prod — junto com suas dependencias
// transitivas pesadas (msw, tough-cookie, tldts via msw, etc).
//
// Se um novo mock for adicionado, registrar a rota AQUI dentro.
// =============================================================================
let devRoutes: RouteObject[] = [];
if (import.meta.env.DEV) {
  // Phase 11 — Mocks (Regra de 3 Mocks). DEV only — strippados do bundle prod.
  const MockBudgetsIndex = lazy(() => import('./__mocks__/reports/budgets/index'));
  const MockBudgetsA = lazy(() => import('./__mocks__/reports/budgets/option-a'));
  const MockBudgetsB = lazy(() => import('./__mocks__/reports/budgets/option-b'));
  const MockBudgetsC = lazy(() => import('./__mocks__/reports/budgets/option-c'));
  const MockForecastsIndex = lazy(() => import('./__mocks__/reports/forecasts/index'));
  const MockForecastsA = lazy(() => import('./__mocks__/reports/forecasts/option-a'));
  const MockForecastsB = lazy(() => import('./__mocks__/reports/forecasts/option-b'));
  const MockForecastsC = lazy(() => import('./__mocks__/reports/forecasts/option-c'));

  // Phase 12 — Mocks notifications (Regra de 3 Mocks). DEV only.
  const MockNotificationsUmbrella = lazy(() => import('./__mocks__/notifications/index'));
  const MockNotifListIndex = lazy(() => import('./__mocks__/notifications/list/index'));
  const MockNotifListA = lazy(() => import('./__mocks__/notifications/list/option-a'));
  const MockNotifListB = lazy(() => import('./__mocks__/notifications/list/option-b'));
  const MockNotifListC = lazy(() => import('./__mocks__/notifications/list/option-c'));
  const MockNotifPrefsIndex = lazy(() => import('./__mocks__/notifications/preferences/index'));
  const MockNotifPrefsA = lazy(() => import('./__mocks__/notifications/preferences/option-a'));
  const MockNotifPrefsB = lazy(() => import('./__mocks__/notifications/preferences/option-b'));
  const MockNotifPrefsC = lazy(() => import('./__mocks__/notifications/preferences/option-c'));

  // Phase 14 — Mocks imports (Regra de 3 Mocks). DEV only.
  const MockImportsUmbrella = lazy(() => import('./__mocks__/imports/index'));
  const MockImportsWizardA = lazy(() => import('./__mocks__/imports/wizard/option-a'));
  const MockImportsWizardB = lazy(() => import('./__mocks__/imports/wizard/option-b'));
  const MockImportsWizardC = lazy(() => import('./__mocks__/imports/wizard/option-c'));
  // Round 2 — fix de layout do wizard no modal (coluna unica + indicador compacto).
  const MockImportsWizardD = lazy(() => import('./__mocks__/imports/wizard/option-d'));
  const MockImportsWizardE = lazy(() => import('./__mocks__/imports/wizard/option-e'));
  const MockImportsWizardF = lazy(() => import('./__mocks__/imports/wizard/option-f'));

  // Phase 15 — Mocks edge states (Regra de 3 Mocks). DEV only.
  const MockEdgeUmbrella = lazy(() => import('./__mocks__/edge/index'));
  const MockEdge404Index = lazy(() => import('./__mocks__/edge/404/index'));
  const MockEdge404A = lazy(() => import('./__mocks__/edge/404/option-a'));
  const MockEdge404B = lazy(() => import('./__mocks__/edge/404/option-b'));
  const MockEdge404C = lazy(() => import('./__mocks__/edge/404/option-c'));
  const MockEdgeErrorBoundaryIndex = lazy(() => import('./__mocks__/edge/error-boundary/index'));
  const MockEdgeErrorBoundaryA = lazy(() => import('./__mocks__/edge/error-boundary/option-a'));
  const MockEdgeErrorBoundaryB = lazy(() => import('./__mocks__/edge/error-boundary/option-b'));
  const MockEdgeErrorBoundaryC = lazy(() => import('./__mocks__/edge/error-boundary/option-c'));
  const MockEdgeOfflineBannerIndex = lazy(() => import('./__mocks__/edge/offline-banner/index'));
  const MockEdgeOfflineBannerA = lazy(() => import('./__mocks__/edge/offline-banner/option-a'));
  const MockEdgeOfflineBannerB = lazy(() => import('./__mocks__/edge/offline-banner/option-b'));
  const MockEdgeOfflineBannerC = lazy(() => import('./__mocks__/edge/offline-banner/option-c'));

  // Paginas legais (Regra de 3 Mocks). DEV only. Layout do documento legal
  // publico (/legal/*) — A long-form, B com TOC, C card header band.
  const MockLegalIndex = lazy(() => import('./__mocks__/legal/index'));
  const MockLegalA = lazy(() => import('./__mocks__/legal/option-a'));
  const MockLegalB = lazy(() => import('./__mocks__/legal/option-b'));
  const MockLegalC = lazy(() => import('./__mocks__/legal/option-c'));
  const MockLegalD = lazy(() => import('./__mocks__/legal/option-d'));

  // Phase 13 — Mocks profile (Regra de 3 Mocks). DEV only.
  const MockProfileUmbrella = lazy(() => import('./__mocks__/profile/index'));
  const MockProfileScreenIndex = lazy(() => import('./__mocks__/profile/screen/index'));
  const MockProfileScreenA = lazy(() => import('./__mocks__/profile/screen/option-a'));
  const MockProfileScreenB = lazy(() => import('./__mocks__/profile/screen/option-b'));
  const MockProfileScreenC = lazy(() => import('./__mocks__/profile/screen/option-c'));
  const MockProfileEditIndex = lazy(() => import('./__mocks__/profile/edit/index'));
  const MockProfileEditA = lazy(() => import('./__mocks__/profile/edit/option-a'));
  const MockProfileEditB = lazy(() => import('./__mocks__/profile/edit/option-b'));
  const MockProfileEditC = lazy(() => import('./__mocks__/profile/edit/option-c'));
  const MockProfileSettingsIndex = lazy(() => import('./__mocks__/profile/settings/index'));
  const MockProfileSettingsA = lazy(() => import('./__mocks__/profile/settings/option-a'));
  const MockProfileSettingsB = lazy(() => import('./__mocks__/profile/settings/option-b'));
  const MockProfileSettingsC = lazy(() => import('./__mocks__/profile/settings/option-c'));
  const MockProfileLgpdIndex = lazy(() => import('./__mocks__/profile/lgpd/index'));
  const MockProfileLgpdA = lazy(() => import('./__mocks__/profile/lgpd/option-a'));
  const MockProfileLgpdB = lazy(() => import('./__mocks__/profile/lgpd/option-b'));
  const MockProfileLgpdC = lazy(() => import('./__mocks__/profile/lgpd/option-c'));

  // Phase 07 — Mocks (Regra de 3 Mocks). DEV only.
  const MockManageCategoriesIndex = lazy(
    () => import('./__mocks__/transactions/manage-categories/index'),
  );
  const MockManageCategoriesA = lazy(
    () => import('./__mocks__/transactions/manage-categories/option-a'),
  );
  const MockManageCategoriesB = lazy(
    () => import('./__mocks__/transactions/manage-categories/option-b'),
  );
  const MockManageCategoriesC = lazy(
    () => import('./__mocks__/transactions/manage-categories/option-c'),
  );

  // Phase 08 — Mocks (Regra de 3 Mocks). DEV only.
  const MockFamilyGroupsUmbrella = lazy(() => import('./__mocks__/family-groups/index'));
  const MockGroupsListIndex = lazy(() => import('./__mocks__/family-groups/list/index'));
  const MockGroupsListA = lazy(() => import('./__mocks__/family-groups/list/option-a'));
  const MockGroupsListB = lazy(() => import('./__mocks__/family-groups/list/option-b'));
  const MockGroupsListC = lazy(() => import('./__mocks__/family-groups/list/option-c'));
  const MockCreateGroupIndex = lazy(() => import('./__mocks__/family-groups/create/index'));
  const MockCreateGroupA = lazy(() => import('./__mocks__/family-groups/create/option-a'));
  const MockCreateGroupB = lazy(() => import('./__mocks__/family-groups/create/option-b'));
  const MockCreateGroupC = lazy(() => import('./__mocks__/family-groups/create/option-c'));
  const MockGroupDetailIndex = lazy(() => import('./__mocks__/family-groups/detail/index'));
  const MockGroupDetailA = lazy(() => import('./__mocks__/family-groups/detail/option-a'));
  const MockGroupDetailB = lazy(() => import('./__mocks__/family-groups/detail/option-b'));
  const MockGroupDetailC = lazy(() => import('./__mocks__/family-groups/detail/option-c'));

  // Phase 09 — Mocks (Regra de 3 Mocks). DEV only.
  const MockBalancesIndex = lazy(() => import('./__mocks__/family-groups/balances/index'));
  const MockBalancesA = lazy(() => import('./__mocks__/family-groups/balances/option-a'));
  const MockBalancesB = lazy(() => import('./__mocks__/family-groups/balances/option-b'));
  const MockBalancesC = lazy(() => import('./__mocks__/family-groups/balances/option-c'));
  const MockContribHistoryIndex = lazy(
    () => import('./__mocks__/family-groups/contribution-history/index'),
  );
  const MockContribHistoryA = lazy(
    () => import('./__mocks__/family-groups/contribution-history/option-a'),
  );
  const MockContribHistoryB = lazy(
    () => import('./__mocks__/family-groups/contribution-history/option-b'),
  );
  const MockContribHistoryC = lazy(
    () => import('./__mocks__/family-groups/contribution-history/option-c'),
  );
  const MockSettlementsListIndex = lazy(
    () => import('./__mocks__/family-groups/settlements-list/index'),
  );
  const MockSettlementsListA = lazy(
    () => import('./__mocks__/family-groups/settlements-list/option-a'),
  );
  const MockSettlementsListB = lazy(
    () => import('./__mocks__/family-groups/settlements-list/option-b'),
  );
  const MockSettlementsListC = lazy(
    () => import('./__mocks__/family-groups/settlements-list/option-c'),
  );
  const MockNewSettlementIndex = lazy(
    () => import('./__mocks__/family-groups/new-settlement/index'),
  );
  const MockNewSettlementA = lazy(
    () => import('./__mocks__/family-groups/new-settlement/option-a'),
  );
  const MockNewSettlementB = lazy(
    () => import('./__mocks__/family-groups/new-settlement/option-b'),
  );
  const MockNewSettlementC = lazy(
    () => import('./__mocks__/family-groups/new-settlement/option-c'),
  );
  const MockSettlementDetailIndex = lazy(
    () => import('./__mocks__/family-groups/settlement-detail/index'),
  );
  const MockSettlementDetailA = lazy(
    () => import('./__mocks__/family-groups/settlement-detail/option-a'),
  );
  const MockSettlementDetailB = lazy(
    () => import('./__mocks__/family-groups/settlement-detail/option-b'),
  );
  const MockSettlementDetailC = lazy(
    () => import('./__mocks__/family-groups/settlement-detail/option-c'),
  );

  // Phase 10 — Mocks (Regra de 3 Mocks). DEV only.
  const MockReportsUmbrella = lazy(() => import('./__mocks__/reports/index'));
  const MockDashboardIndex = lazy(() => import('./__mocks__/reports/dashboard/index'));
  const MockDashboardA = lazy(() => import('./__mocks__/reports/dashboard/option-a'));
  const MockDashboardB = lazy(() => import('./__mocks__/reports/dashboard/option-b'));
  const MockDashboardC = lazy(() => import('./__mocks__/reports/dashboard/option-c'));
  const MockHistoryIndex = lazy(() => import('./__mocks__/reports/history/index'));
  const MockHistoryA = lazy(() => import('./__mocks__/reports/history/option-a'));
  const MockHistoryB = lazy(() => import('./__mocks__/reports/history/option-b'));
  const MockHistoryC = lazy(() => import('./__mocks__/reports/history/option-c'));

  // Fix sobreposicao Receitas/Despesas no balance-card — Mocks (Regra de 3
  // Mocks). DEV only.
  const MockBalanceCardIndex = lazy(() => import('./__mocks__/transactions/balance-card/index'));
  const MockBalanceCardA = lazy(() => import('./__mocks__/transactions/balance-card/option-a'));
  const MockBalanceCardB = lazy(() => import('./__mocks__/transactions/balance-card/option-b'));
  const MockBalanceCardC = lazy(() => import('./__mocks__/transactions/balance-card/option-c'));

  // Mocks manage-categories Round 2 — ALTURA do sheet deslizante (D/E/F).
  const MockCatSheetD = lazy(() => import('./__mocks__/transactions/manage-categories/option-d'));
  const MockCatSheetE = lazy(() => import('./__mocks__/transactions/manage-categories/option-e'));
  const MockCatSheetF = lazy(() => import('./__mocks__/transactions/manage-categories/option-f'));

  // Mocks /app/links — landing de detecção de SO (Regra de 3 Mocks).
  const MockAppLinksIndex = lazy(() => import('./__mocks__/app-links/index'));
  const MockAppLinksA = lazy(() => import('./__mocks__/app-links/option-a'));
  const MockAppLinksB = lazy(() => import('./__mocks__/app-links/option-b'));
  const MockAppLinksC = lazy(() => import('./__mocks__/app-links/option-c'));

  // Mocks menu mobile (redesign do nav mobile — Regra de 3 Mocks).
  const MockMobileNavIndex = lazy(() => import('./__mocks__/mobile-nav/index'));
  const MockMobileNavA = lazy(() => import('./__mocks__/mobile-nav/option-a'));
  const MockMobileNavB = lazy(() => import('./__mocks__/mobile-nav/option-b'));
  const MockMobileNavC = lazy(() => import('./__mocks__/mobile-nav/option-c'));

  devRoutes = [
    { path: '__dev__/components', element: <GallerySuspense /> },
    // Mocks menu mobile (redesign do nav — A/B/C).
    { path: '__mocks__/mobile-nav', element: withSuspense(MockMobileNavIndex, 'app')({}) },
    { path: '__mocks__/mobile-nav/option-a', element: withSuspense(MockMobileNavA, 'app')({}) },
    { path: '__mocks__/mobile-nav/option-b', element: withSuspense(MockMobileNavB, 'app')({}) },
    { path: '__mocks__/mobile-nav/option-c', element: withSuspense(MockMobileNavC, 'app')({}) },
    // Mocks /app/links — landing detecção de SO (A/B/C).
    { path: '__mocks__/app-links', element: withSuspense(MockAppLinksIndex, 'app')({}) },
    { path: '__mocks__/app-links/option-a', element: withSuspense(MockAppLinksA, 'app')({}) },
    { path: '__mocks__/app-links/option-b', element: withSuspense(MockAppLinksB, 'app')({}) },
    { path: '__mocks__/app-links/option-c', element: withSuspense(MockAppLinksC, 'app')({}) },
    // Mocks Phase 07 — manage-categories (A/B/C).
    {
      path: '__mocks__/transactions/manage-categories',
      element: withSuspense(MockManageCategoriesIndex, 'app')({}),
    },
    {
      path: '__mocks__/transactions/manage-categories/option-a',
      element: withSuspense(MockManageCategoriesA, 'app')({}),
    },
    {
      path: '__mocks__/transactions/manage-categories/option-b',
      element: withSuspense(MockManageCategoriesB, 'app')({}),
    },
    {
      path: '__mocks__/transactions/manage-categories/option-c',
      element: withSuspense(MockManageCategoriesC, 'app')({}),
    },
    // Round 2 — altura do sheet deslizante (D/E/F).
    {
      path: '__mocks__/transactions/manage-categories/option-d',
      element: withSuspense(MockCatSheetD, 'app')({}),
    },
    {
      path: '__mocks__/transactions/manage-categories/option-e',
      element: withSuspense(MockCatSheetE, 'app')({}),
    },
    {
      path: '__mocks__/transactions/manage-categories/option-f',
      element: withSuspense(MockCatSheetF, 'app')({}),
    },
    // Fix balance-card — sobreposicao Receitas/Despesas (A/B/C).
    {
      path: '__mocks__/transactions/balance-card',
      element: withSuspense(MockBalanceCardIndex, 'app')({}),
    },
    {
      path: '__mocks__/transactions/balance-card/option-a',
      element: withSuspense(MockBalanceCardA, 'app')({}),
    },
    {
      path: '__mocks__/transactions/balance-card/option-b',
      element: withSuspense(MockBalanceCardB, 'app')({}),
    },
    {
      path: '__mocks__/transactions/balance-card/option-c',
      element: withSuspense(MockBalanceCardC, 'app')({}),
    },
    // Phase 08 — family-groups mocks (Regra de 3 Mocks).
    {
      path: '__mocks__/family-groups',
      element: withSuspense(MockFamilyGroupsUmbrella, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/list',
      element: withSuspense(MockGroupsListIndex, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/list/option-a',
      element: withSuspense(MockGroupsListA, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/list/option-b',
      element: withSuspense(MockGroupsListB, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/list/option-c',
      element: withSuspense(MockGroupsListC, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/create',
      element: withSuspense(MockCreateGroupIndex, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/create/option-a',
      element: withSuspense(MockCreateGroupA, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/create/option-b',
      element: withSuspense(MockCreateGroupB, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/create/option-c',
      element: withSuspense(MockCreateGroupC, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/detail',
      element: withSuspense(MockGroupDetailIndex, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/detail/option-a',
      element: withSuspense(MockGroupDetailA, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/detail/option-b',
      element: withSuspense(MockGroupDetailB, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/detail/option-c',
      element: withSuspense(MockGroupDetailC, 'app')({}),
    },
    // Phase 09 — Mocks (Regra de 3 Mocks).
    {
      path: '__mocks__/family-groups/balances',
      element: withSuspense(MockBalancesIndex, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/balances/option-a',
      element: withSuspense(MockBalancesA, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/balances/option-b',
      element: withSuspense(MockBalancesB, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/balances/option-c',
      element: withSuspense(MockBalancesC, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/contribution-history',
      element: withSuspense(MockContribHistoryIndex, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/contribution-history/option-a',
      element: withSuspense(MockContribHistoryA, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/contribution-history/option-b',
      element: withSuspense(MockContribHistoryB, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/contribution-history/option-c',
      element: withSuspense(MockContribHistoryC, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/settlements-list',
      element: withSuspense(MockSettlementsListIndex, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/settlements-list/option-a',
      element: withSuspense(MockSettlementsListA, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/settlements-list/option-b',
      element: withSuspense(MockSettlementsListB, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/settlements-list/option-c',
      element: withSuspense(MockSettlementsListC, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/new-settlement',
      element: withSuspense(MockNewSettlementIndex, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/new-settlement/option-a',
      element: withSuspense(MockNewSettlementA, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/new-settlement/option-b',
      element: withSuspense(MockNewSettlementB, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/new-settlement/option-c',
      element: withSuspense(MockNewSettlementC, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/settlement-detail',
      element: withSuspense(MockSettlementDetailIndex, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/settlement-detail/option-a',
      element: withSuspense(MockSettlementDetailA, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/settlement-detail/option-b',
      element: withSuspense(MockSettlementDetailB, 'app')({}),
    },
    {
      path: '__mocks__/family-groups/settlement-detail/option-c',
      element: withSuspense(MockSettlementDetailC, 'app')({}),
    },
    // Phase 10 — Mocks (Regra de 3 Mocks).
    {
      path: '__mocks__/reports',
      element: withSuspense(MockReportsUmbrella, 'app')({}),
    },
    {
      path: '__mocks__/reports/dashboard',
      element: withSuspense(MockDashboardIndex, 'app')({}),
    },
    {
      path: '__mocks__/reports/dashboard/option-a',
      element: withSuspense(MockDashboardA, 'app')({}),
    },
    {
      path: '__mocks__/reports/dashboard/option-b',
      element: withSuspense(MockDashboardB, 'app')({}),
    },
    {
      path: '__mocks__/reports/dashboard/option-c',
      element: withSuspense(MockDashboardC, 'app')({}),
    },
    {
      path: '__mocks__/reports/history',
      element: withSuspense(MockHistoryIndex, 'app')({}),
    },
    {
      path: '__mocks__/reports/history/option-a',
      element: withSuspense(MockHistoryA, 'app')({}),
    },
    {
      path: '__mocks__/reports/history/option-b',
      element: withSuspense(MockHistoryB, 'app')({}),
    },
    {
      path: '__mocks__/reports/history/option-c',
      element: withSuspense(MockHistoryC, 'app')({}),
    },
    // Phase 11 — Mocks budgets (Regra de 3 Mocks).
    {
      path: '__mocks__/reports/budgets',
      element: withSuspense(MockBudgetsIndex, 'app')({}),
    },
    {
      path: '__mocks__/reports/budgets/option-a',
      element: withSuspense(MockBudgetsA, 'app')({}),
    },
    {
      path: '__mocks__/reports/budgets/option-b',
      element: withSuspense(MockBudgetsB, 'app')({}),
    },
    {
      path: '__mocks__/reports/budgets/option-c',
      element: withSuspense(MockBudgetsC, 'app')({}),
    },
    // Phase 11 — Mocks forecasts (Regra de 3 Mocks).
    {
      path: '__mocks__/reports/forecasts',
      element: withSuspense(MockForecastsIndex, 'app')({}),
    },
    {
      path: '__mocks__/reports/forecasts/option-a',
      element: withSuspense(MockForecastsA, 'app')({}),
    },
    {
      path: '__mocks__/reports/forecasts/option-b',
      element: withSuspense(MockForecastsB, 'app')({}),
    },
    {
      path: '__mocks__/reports/forecasts/option-c',
      element: withSuspense(MockForecastsC, 'app')({}),
    },
    // Phase 12 — Mocks notifications (Regra de 3 Mocks).
    {
      path: '__mocks__/notifications',
      element: withSuspense(MockNotificationsUmbrella, 'app')({}),
    },
    {
      path: '__mocks__/notifications/list',
      element: withSuspense(MockNotifListIndex, 'app')({}),
    },
    {
      path: '__mocks__/notifications/list/option-a',
      element: withSuspense(MockNotifListA, 'app')({}),
    },
    {
      path: '__mocks__/notifications/list/option-b',
      element: withSuspense(MockNotifListB, 'app')({}),
    },
    {
      path: '__mocks__/notifications/list/option-c',
      element: withSuspense(MockNotifListC, 'app')({}),
    },
    {
      path: '__mocks__/notifications/preferences',
      element: withSuspense(MockNotifPrefsIndex, 'app')({}),
    },
    {
      path: '__mocks__/notifications/preferences/option-a',
      element: withSuspense(MockNotifPrefsA, 'app')({}),
    },
    {
      path: '__mocks__/notifications/preferences/option-b',
      element: withSuspense(MockNotifPrefsB, 'app')({}),
    },
    {
      path: '__mocks__/notifications/preferences/option-c',
      element: withSuspense(MockNotifPrefsC, 'app')({}),
    },
    // Phase 13 — Mocks profile (Regra de 3 Mocks).
    {
      path: '__mocks__/profile',
      element: withSuspense(MockProfileUmbrella, 'app')({}),
    },
    {
      path: '__mocks__/profile/screen',
      element: withSuspense(MockProfileScreenIndex, 'app')({}),
    },
    {
      path: '__mocks__/profile/screen/option-a',
      element: withSuspense(MockProfileScreenA, 'app')({}),
    },
    {
      path: '__mocks__/profile/screen/option-b',
      element: withSuspense(MockProfileScreenB, 'app')({}),
    },
    {
      path: '__mocks__/profile/screen/option-c',
      element: withSuspense(MockProfileScreenC, 'app')({}),
    },
    {
      path: '__mocks__/profile/edit',
      element: withSuspense(MockProfileEditIndex, 'app')({}),
    },
    {
      path: '__mocks__/profile/edit/option-a',
      element: withSuspense(MockProfileEditA, 'app')({}),
    },
    {
      path: '__mocks__/profile/edit/option-b',
      element: withSuspense(MockProfileEditB, 'app')({}),
    },
    {
      path: '__mocks__/profile/edit/option-c',
      element: withSuspense(MockProfileEditC, 'app')({}),
    },
    {
      path: '__mocks__/profile/settings',
      element: withSuspense(MockProfileSettingsIndex, 'app')({}),
    },
    {
      path: '__mocks__/profile/settings/option-a',
      element: withSuspense(MockProfileSettingsA, 'app')({}),
    },
    {
      path: '__mocks__/profile/settings/option-b',
      element: withSuspense(MockProfileSettingsB, 'app')({}),
    },
    {
      path: '__mocks__/profile/settings/option-c',
      element: withSuspense(MockProfileSettingsC, 'app')({}),
    },
    {
      path: '__mocks__/profile/lgpd',
      element: withSuspense(MockProfileLgpdIndex, 'app')({}),
    },
    {
      path: '__mocks__/profile/lgpd/option-a',
      element: withSuspense(MockProfileLgpdA, 'app')({}),
    },
    {
      path: '__mocks__/profile/lgpd/option-b',
      element: withSuspense(MockProfileLgpdB, 'app')({}),
    },
    {
      path: '__mocks__/profile/lgpd/option-c',
      element: withSuspense(MockProfileLgpdC, 'app')({}),
    },
    // Phase 14 — Mocks imports (Regra de 3 Mocks).
    {
      path: '__mocks__/imports',
      element: withSuspense(MockImportsUmbrella, 'app')({}),
    },
    {
      path: '__mocks__/imports/wizard/option-a',
      element: withSuspense(MockImportsWizardA, 'app')({}),
    },
    {
      path: '__mocks__/imports/wizard/option-b',
      element: withSuspense(MockImportsWizardB, 'app')({}),
    },
    {
      path: '__mocks__/imports/wizard/option-c',
      element: withSuspense(MockImportsWizardC, 'app')({}),
    },
    {
      path: '__mocks__/imports/wizard/option-d',
      element: withSuspense(MockImportsWizardD, 'app')({}),
    },
    {
      path: '__mocks__/imports/wizard/option-e',
      element: withSuspense(MockImportsWizardE, 'app')({}),
    },
    {
      path: '__mocks__/imports/wizard/option-f',
      element: withSuspense(MockImportsWizardF, 'app')({}),
    },
    // Phase 15 — Mocks edge states (Regra de 3 Mocks).
    {
      path: '__mocks__/edge',
      element: withSuspense(MockEdgeUmbrella, 'app')({}),
    },
    {
      path: '__mocks__/edge/404',
      element: withSuspense(MockEdge404Index, 'app')({}),
    },
    {
      path: '__mocks__/edge/404/option-a',
      element: withSuspense(MockEdge404A, 'auth')({}),
    },
    {
      path: '__mocks__/edge/404/option-b',
      element: withSuspense(MockEdge404B, 'auth')({}),
    },
    {
      path: '__mocks__/edge/404/option-c',
      element: withSuspense(MockEdge404C, 'auth')({}),
    },
    {
      path: '__mocks__/edge/error-boundary',
      element: withSuspense(MockEdgeErrorBoundaryIndex, 'app')({}),
    },
    {
      path: '__mocks__/edge/error-boundary/option-a',
      element: withSuspense(MockEdgeErrorBoundaryA, 'auth')({}),
    },
    {
      path: '__mocks__/edge/error-boundary/option-b',
      element: withSuspense(MockEdgeErrorBoundaryB, 'auth')({}),
    },
    {
      path: '__mocks__/edge/error-boundary/option-c',
      element: withSuspense(MockEdgeErrorBoundaryC, 'auth')({}),
    },
    {
      path: '__mocks__/edge/offline-banner',
      element: withSuspense(MockEdgeOfflineBannerIndex, 'app')({}),
    },
    {
      path: '__mocks__/edge/offline-banner/option-a',
      element: withSuspense(MockEdgeOfflineBannerA, 'auth')({}),
    },
    {
      path: '__mocks__/edge/offline-banner/option-b',
      element: withSuspense(MockEdgeOfflineBannerB, 'auth')({}),
    },
    {
      path: '__mocks__/edge/offline-banner/option-c',
      element: withSuspense(MockEdgeOfflineBannerC, 'auth')({}),
    },
    // Paginas legais (Regra de 3 Mocks).
    {
      path: '__mocks__/legal',
      element: withSuspense(MockLegalIndex, 'app')({}),
    },
    {
      path: '__mocks__/legal/option-a',
      element: withSuspense(MockLegalA, 'auth')({}),
    },
    {
      path: '__mocks__/legal/option-b',
      element: withSuspense(MockLegalB, 'auth')({}),
    },
    {
      path: '__mocks__/legal/option-c',
      element: withSuspense(MockLegalC, 'auth')({}),
    },
    {
      path: '__mocks__/legal/option-d',
      element: withSuspense(MockLegalD, 'auth')({}),
    },
  ];
}

export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    children: [
      { index: true, element: <RootRedirect /> },
      // Rota PUBLICA standalone (sem AuthLayout/AppLayout): landing de
      // deteccao de plataforma. No PC redireciona p/ /welcome; no celular
      // mostra "em breve na loja" + acesso a versao web. Sem guard de auth.
      { path: 'app/links', element: <AppLinks /> },
      { element: <AuthLayout />, children: authRoutes },
      { element: <AppLayout />, children: appRoutes },
      { element: <LegalLayout />, children: legalRoutes },
      ...devRoutes,
      { path: '*', element: <NotFound /> },
    ],
  },
];
